import { sanitizeText } from '@shared/utils/security';
import { Toast } from '../Toast';

export class StatusManager {
  private element: HTMLElement;
  private statusTextElement: HTMLElement | null;
  private liveBadgeElement: HTMLElement | null;
  private sessionTimerInterval: number | null;
  private sessionEndTime: number | null;
  private notifiedThreeMinutes = false;
  private notifiedExpired = false;

  constructor(panelElement: HTMLElement) {
    this.element = panelElement;
    this.statusTextElement = this.element.querySelector('[data-ce="status"] span');
    this.liveBadgeElement = this.element.querySelector('.ce-badge');
    this.sessionTimerInterval = null;
    this.sessionEndTime = null;
  }

  setStatus(message: string): void {
    if (this.statusTextElement) {
      this.statusTextElement.textContent = sanitizeText(message);
    }
  }

  setSessionTimer(expiresAt: string): void {
    this.sessionEndTime = new Date(expiresAt).getTime();

    if (this.sessionTimerInterval) {
      clearInterval(this.sessionTimerInterval);
    }

    if (!this.liveBadgeElement) {
      return;
    }

    this.updateSessionTimer();
    this.sessionTimerInterval = window.setInterval(() => {
      this.updateSessionTimer();
    }, 1000);
  }

  private updateSessionTimer(): void {
    if (!this.sessionEndTime || !this.liveBadgeElement) return;

    const now = Date.now();
    const remaining = this.sessionEndTime - now;

    if (remaining <= 0) {
      this.liveBadgeElement.textContent = 'EXPIRED';
      this.liveBadgeElement.style.backgroundColor = '#dc2626';
      this.liveBadgeElement.style.color = '#ffffff';
      this.liveBadgeElement.style.fontWeight = 'bold';
      this.liveBadgeElement.style.fontSize = '11px';
      this.liveBadgeElement.style.padding = '2px 6px';
      this.liveBadgeElement.style.display = 'block';

      // Show expiry notification once
      if (!this.notifiedExpired) {
        Toast.show('Session expired! Please refresh the page to continue.', 'error', 5000);
        this.notifiedExpired = true;
      }

      this.stopSessionTimer();
      return;
    }

    // Only show timer when less than 3 minutes remaining
    if (remaining > 180000) {
      this.liveBadgeElement.style.display = 'none';
      return;
    }

    this.liveBadgeElement.style.display = 'block';

    const minutes = Math.floor(remaining / 60000);
    const seconds = Math.floor((remaining % 60000) / 1000);

    if (minutes > 0) {
      this.liveBadgeElement.textContent = `${minutes}m ${seconds}s`;
    } else {
      this.liveBadgeElement.textContent = `${seconds}s`;
    }

    // Enhanced styling for better visibility
    this.liveBadgeElement.style.color = '#ffffff';
    this.liveBadgeElement.style.fontWeight = 'bold';
    this.liveBadgeElement.style.fontSize = '11px';
    this.liveBadgeElement.style.padding = '2px 6px';
    this.liveBadgeElement.style.borderRadius = '4px';

    // Show warning when less than 5 minutes
    if (remaining < 300000) {
      this.liveBadgeElement.style.backgroundColor = '#f59e0b';
      this.liveBadgeElement.style.animation = 'pulse 1s infinite';
    }

    // Show urgent warning when less than 1 minute
    if (remaining < 60000) {
      this.liveBadgeElement.style.backgroundColor = '#ef4444';
      this.liveBadgeElement.style.animation = 'pulse 0.5s infinite';
    }

    // Normal state when plenty of time (but we only show this in last 3 minutes)
    if (remaining >= 300000) {
      this.liveBadgeElement.style.backgroundColor = '#10b981';
      this.liveBadgeElement.style.animation = 'none';
    }

    // Show 3-minute warning notification once
    if (remaining <= 180000 && remaining > 160000 && !this.notifiedThreeMinutes) {
      Toast.show('Session will expire in 3 minutes. Save your work soon!', 'warning', 4000);
      this.notifiedThreeMinutes = true;
    }
  }

  stopSessionTimer(): void {
    if (this.sessionTimerInterval) {
      clearInterval(this.sessionTimerInterval);
      this.sessionTimerInterval = null;
    }
  }

  getStatus(): string {
    return this.statusTextElement?.textContent || '';
  }

  isErrorState(): boolean {
    const currentStatus = this.getStatus();
    return (
      currentStatus.toLowerCase().includes('failed') ||
      currentStatus.toLowerCase().includes('expired') ||
      currentStatus.toLowerCase().includes('invalid') ||
      currentStatus.toLowerCase().includes('error')
    );
  }

  destroy(): void {
    this.stopSessionTimer();
  }
}
